import './YourLists.css';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineTrash, HiOutlinePencil, HiOutlinePlus, HiOutlineBookmark, HiOutlineX, HiOutlineShare, HiOutlineDotsVertical, HiOutlineExternalLink, HiOutlineUserGroup, HiOutlineGlobe } from 'react-icons/hi';
import { fetchAllLists, removeFromList, deleteList, renameList, createList, searchUsers, shareList, fetchSharedUsers, unshareList, updateSharePermission, updateOrgAccess, ListSummary, ListItem, ShareUser, SharedEntry, StorePoiDetail } from '../../services/listService';
import { calculateStoreRecommendations } from '../../services/brandSitesService';
import Spinner from '../../components/Spinner/Spinner';
import CustomDropdown from '../../components/CustomDropdown/CustomDropdown';
import toast from 'react-hot-toast';
import useAuthStore from '../../stores/authStore';
import { useConfigurationStore } from '../../stores';

const buildConditionsMap = (config: any) => {
  const level = config.activeReadiness?.[0] || 'high';
  return config.conditions.reduce((acc: Record<string, string>, cond: any) => {
    acc[cond.metric] = cond[level];
    return acc;
  }, {});
};

function YourLists() {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const { purchek, cartManager, containment, subscription, pairedWheel } = useConfigurationStore();
  const [myLists, setMyLists] = useState<ListSummary[]>([]);
  const [sharedLists, setSharedLists] = useState<ListSummary[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'mine' | 'shared'>('all');
  const [loading, setLoading] = useState(true);
  const [detailList, setDetailList] = useState<string | null>(null);
  const [renamingList, setRenamingList] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const [showNewListModal, setShowNewListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListType, setNewListType] = useState<'brand' | 'store'>('brand');

  const [shareListName, setShareListName] = useState<string | null>(null);
  const [shareListId, setShareListId] = useState<string | null>(null);
  const [shareSearch, setShareSearch] = useState('');
  const [shareSuggestions, setShareSuggestions] = useState<ShareUser[]>([]);
  const [sharedWith, setSharedWith] = useState<SharedEntry[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [shareDefaultPermission, setShareDefaultPermission] = useState<'view' | 'edit'>('view');
  const [shareOrgAccess, setShareOrgAccess] = useState<'view' | 'edit' | null>(null);

  const ownerName = user?.email
    ? user.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
    : 'You';

  const purchekActiveConditions = useMemo(() => buildConditionsMap(purchek), [purchek]);
  const cartManagerActiveConditions = useMemo(() => buildConditionsMap(cartManager), [cartManager]);
  const containmentActiveConditions = useMemo(() => buildConditionsMap(containment), [containment]);
  const subscriptionActiveConditions = useMemo(() => buildConditionsMap(subscription), [subscription]);
  const pairedWheelActiveConditions = useMemo(() => buildConditionsMap(pairedWheel), [pairedWheel]);

  const getOpportunityCount = (poi: StorePoiDetail) => {
    const recs = calculateStoreRecommendations(
      poi, 0,
      purchekActiveConditions, cartManagerActiveConditions,
      containmentActiveConditions, subscriptionActiveConditions,
      pairedWheelActiveConditions
    );
    let count = 0;
    if (recs.purchek.length > 0) count++;
    if (recs.containment.length > 0) count++;
    if (recs.cartManager.length > 0) count++;
    if (recs.subscription.length > 0) count++;
    if (recs.pairedWheel.length > 0) count++;
    return count;
  };

  const getStoreStats = (list: ListSummary) => {
    if (list.list_type !== 'store' || !list.storeDetails) return null;
    let totalOpps = 0;
    let criticalCount = 0;
    list.items.forEach((item: any) => {
      const poi = list.storeDetails?.get(item.poi_id || '');
      if (poi) {
        totalOpps += getOpportunityCount(poi);
        if ((poi.geo?.total_crime_incidents ?? 0) >= 5000) criticalCount++;
      }
    });
    return { sites: list.count, opportunities: totalOpps, critical: criticalCount };
  };

  const getBrandStats = (list: ListSummary) => {
    if (list.list_type !== 'brand' || !list.brandDetails) return null;
    let totalStores = 0;
    let occupiedStores = 0;
    list.items.forEach((item: any) => {
      const brand = list.brandDetails?.get(item.brand_name || '');
      if (brand) {
        totalStores += brand.total_stores;
        occupiedStores += brand.occupied_stores;
      }
    });
    return { brands: list.count, totalStores, occupiedStores };
  };

  const handleShare = (e: React.MouseEvent, list: ListSummary) => {
    e.stopPropagation();
    setMenuOpen(null);
    setShareListName(list.list_name);
    setShareListId(list.list_id || null);
    setShareSearch('');
    setShareSuggestions([]);
    setShareDefaultPermission('view');
    setShareOrgAccess(list.org_access || null);
    setShareLoading(true);
    fetchSharedUsers(list.list_name).then((entries: any) => {
      setSharedWith(entries);
      setShareLoading(false);
    });
  };

  const handleShareSearchChange = async (value: string) => {
    setShareSearch(value);
    if (value.trim().length < 2) {
      setShareSuggestions([]);
      return;
    }
    setSearchLoading(true);
    const users = await searchUsers(value.trim());
    const alreadySharedIds = new Set(sharedWith.map(s => s.shared_with));
    setShareSuggestions(users.filter((u: any) => !alreadySharedIds.has(u.id)));
    setSearchLoading(false);
  };

  const handleShareWithUser = async (targetUser: ShareUser) => {
    if (!shareListName) return;
    const result = await shareList(shareListName, targetUser.id, shareDefaultPermission);
    if (result.success) {
      toast.success(`Shared with ${targetUser.name || targetUser.email}`);
      setShareSearch('');
      setShareSuggestions([]);
      const entries = await fetchSharedUsers(shareListName);
      setSharedWith(entries);
    } else {
      toast.error(result.error || 'Failed to share');
    }
  };

  const handlePermissionChange = async (shareId: string, permission: 'view' | 'edit') => {
    const result = await updateSharePermission(shareId, permission);
    if (result.success) {
      setSharedWith(prev => prev.map(s => s.id === shareId ? { ...s, permission } : s));
    } else {
      toast.error(result.error || 'Failed to update permission');
    }
  };

  const handleOrgAccessChange = async (value: 'view' | 'edit' | null) => {
    if (!shareListName) return;
    const result = await updateOrgAccess(shareListName, value);
    if (result.success) {
      setShareOrgAccess(value);
      setMyLists(prev => prev.map(l =>
        l.list_name === shareListName ? { ...l, org_access: value } : l
      ));
      toast.success(value ? `Organization access set to ${value}` : 'Organization access removed');
    } else {
      toast.error(result.error || 'Failed to update');
    }
  };

  const handleUnshare = async (shareId: string) => {
    const result = await unshareList(shareId);
    if (result.success) {
      setSharedWith(prev => prev.filter(s => s.id !== shareId));
      toast.success('Access removed');
    } else {
      toast.error(result.error || 'Failed to remove');
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = async () => {
    setLoading(true);
    const { myLists: mine, sharedLists: shared } = await fetchAllLists();
    setMyLists(mine);
    setSharedLists(shared);
    setLoading(false);
  };

  const lists = activeFilter === 'mine' ? myLists
    : activeFilter === 'shared' ? sharedLists
    : [...myLists, ...sharedLists];

  const getListKey = (list: ListSummary) => `${list.list_name}-${list.isShared ? 'shared' : 'mine'}`;
  const getListByKey = (key: string) => lists.find(l => getListKey(l) === key);

  const handleDeleteList = async (listName: string, key: string) => {
    const result = await deleteList(listName);
    if (result.success) {
      setMyLists(prev => prev.filter(list => list.list_name !== listName));
      if (detailList === key) setDetailList(null);
      setDeleteConfirm(null);
      setMenuOpen(null);
      toast.success('List deleted');
    } else {
      toast.error(result.error || 'Failed to delete list');
    }
  };

  const handleRemoveItem = async (id: string, listName: string) => {
    const result = await removeFromList(id);
    if (result.success) {
      setMyLists(prev => prev.map(list =>
        list.list_name === listName
          ? { ...list, count: list.count - 1, items: list.items.filter((item: any) => item.id !== id) }
          : list
      ));
      toast.success('Item removed');
    } else {
      toast.error(result.error || 'Failed to remove item');
    }
  };

  const handleStartRename = (e: React.MouseEvent, key: string, listName: string) => {
    e.stopPropagation();
    setMenuOpen(null);
    setRenamingList(key);
    setRenameValue(listName);
  };

  const handleRenameSubmit = async () => {
    if (!renamingList) { setRenamingList(null); return; }
    const list = getListByKey(renamingList);
    if (!list || !renameValue.trim() || renameValue.trim() === list.list_name) {
      setRenamingList(null);
      return;
    }

    const oldName = list.list_name;
    const result = await renameList(oldName, renameValue.trim());
    if (result.success) {
      setMyLists(prev => prev.map(l =>
        l.list_name === oldName
          ? { ...l, list_name: renameValue.trim() }
          : l
      ));
      if (detailList === renamingList) {
        setDetailList(`${renameValue.trim()}-mine`);
      }
      toast.success('List renamed');
    } else {
      toast.error(result.error || 'Failed to rename list');
    }
    setRenamingList(null);
  };

  const handleOpenNewListModal = () => {
    setNewListName('');
    setNewListDescription('');
    setNewListType('brand');
    setShowNewListModal(true);
  };

  const handleCreateNewList = async () => {
    const name = newListName.trim();
    if (!name) {
      toast.error('Please enter a list name');
      return;
    }

    if (lists.some(l => l.list_name.toLowerCase() === name.toLowerCase())) {
      toast.error('A list with this name already exists');
      return;
    }

    const result = await createList(name, newListType, newListDescription.trim() || undefined);
    if (result.success) {
      setMyLists(prev => [...prev, { list_name: name, description: newListDescription.trim() || null, list_type: newListType, count: 0, items: [], list_id: result.listId }]);
      setShowNewListModal(false);
      setNewListName('');
      setNewListDescription('');
      setNewListType('brand');
      toast.success(`List "${name}" created`);
    } else {
      toast.error(result.error || 'Failed to create list');
    }
  };

  const handleItemClick = (item: ListItem) => {
    if (item.type === 'brand' && item.brand_name) {
      navigate(`/dashboard/opportunitydetails/${encodeURIComponent(item.brand_name)}?type=ultimateparent`);
    } else if (item.type === 'store' && item.poi_id) {
      navigate(`/storemap/${item.poi_id}`);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getLatestDate = (items: ListItem[]) => {
    if (items.length === 0) return 'No items yet';
    const sorted = [...items].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return `Updated ${formatDate(sorted[0].created_at)}`;
  };

  const openDetail = (list: ListSummary) => {
    if (list.list_type === 'store') {
      if (!list.list_id) {
        toast.error('Unable to open list');
        return;
      }
      navigate(`/dashboard/list-stores/${list.list_id}`);
      return;
    }
    setDetailList(getListKey(list));
  };

  const toggleMenu = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    setMenuOpen(menuOpen === key ? null : key);
    setDeleteConfirm(null);
  };

  const detailListData = detailList ? getListByKey(detailList) : null;

  if (loading) {
    return (
      <div className="yl-container">
        <div className="yl-loading">
          <Spinner size="small" />
        </div>
      </div>
    );
  }

  return (
    <div className="yl-container" onClick={() => { setMenuOpen(null); setDeleteConfirm(null); }}>
      <div className="yl-page-header">
        <div className="yl-header-left">
          <h1 className="yl-title">My Lists</h1>
          <p className="yl-subtitle">Organize and track your saved brands and stores. View opportunity insights for your store lists.</p>
        </div>
        <button className="yl-new-list-btn" onClick={handleOpenNewListModal}>
          <HiOutlinePlus size={16} />
          <span>New list</span>
        </button>
      </div>

      {(myLists.length > 0 || sharedLists.length > 0) && (
        <div className="yl-filter-tabs">
          <button className={`yl-filter-tab ${activeFilter === 'all' ? 'active' : ''}`} onClick={() => setActiveFilter('all')}>
            All <span className="yl-filter-count">{myLists.length + sharedLists.length}</span>
          </button>
          <button className={`yl-filter-tab ${activeFilter === 'mine' ? 'active' : ''}`} onClick={() => setActiveFilter('mine')}>
            My Lists <span className="yl-filter-count">{myLists.length}</span>
          </button>
          <button className={`yl-filter-tab ${activeFilter === 'shared' ? 'active' : ''}`} onClick={() => setActiveFilter('shared')}>
            Shared with me <span className="yl-filter-count">{sharedLists.length}</span>
          </button>
        </div>
      )}

      {lists.length === 0 && (
        <div className="yl-empty-state">
          <HiOutlineBookmark size={48} />
          <h2>No lists yet</h2>
          <p>Create a new list or save brands and stores from the Opportunities page.</p>
          <button className="yl-new-list-btn" onClick={handleOpenNewListModal}>
            <HiOutlinePlus size={16} />
            <span>Create your first list</span>
          </button>
        </div>
      )}

      {lists.length > 0 && (
        <div className="yl-cards-grid">
          {lists.map(list => {
            const storeStats = getStoreStats(list);
            const brandStats = getBrandStats(list);
            const cardKey = getListKey(list);
            return (
              <div key={cardKey} className="yl-card" onClick={() => openDetail(list)}>
                <div className="yl-card-topbar">
                  <div className="yl-card-owner">
                    <span className="yl-card-owner-avatar">
                      {list.isShared
                        ? (list.sharedByName || '?').charAt(0).toUpperCase()
                        : ownerName.charAt(0)}
                    </span>
                    <span className="yl-card-owner-name">
                      {list.isShared ? list.sharedByName : ownerName}
                    </span>
                    {list.isShared && (
                      <span className="yl-shared-badge">
                        {list.sharedPermission === 'edit' ? 'Can edit' : 'View only'}
                      </span>
                    )}
                  </div>
                  {!list.isShared && (
                    <button className="yl-card-share-icon" onClick={e => handleShare(e, list)} title="Shared with">
                      <HiOutlineUserGroup size={15} />
                      {(list.sharedCount || 0) > 0 && (
                        <span className="yl-share-count">{list.sharedCount}</span>
                      )}
                    </button>
                  )}
                </div>

                <div className="yl-card-header">
                  <div className="yl-card-title-section">
                    {renamingList === cardKey ? (
                      <input
                        type="text"
                        className="yl-rename-input"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRenameSubmit();
                          if (e.key === 'Escape') setRenamingList(null);
                        }}
                        onBlur={handleRenameSubmit}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      <h3 className="yl-card-title">{list.list_name}</h3>
                    )}
                    <span className="yl-card-meta">
                      {getLatestDate(list.items)}
                    </span>
                  </div>
                </div>

                {storeStats && (
                  <div className="yl-card-stats">
                    <div className="yl-stat-box">
                      <span className="yl-stat-value">{storeStats.sites}</span>
                      <span className="yl-stat-label">Sites</span>
                    </div>
                    <div className="yl-stat-box">
                      <span className="yl-stat-value yl-stat-opp">{storeStats.opportunities}</span>
                      <span className="yl-stat-label">Opportunities</span>
                    </div>
                    <div className="yl-stat-box">
                      <span className="yl-stat-value yl-stat-critical">{storeStats.critical}</span>
                      <span className="yl-stat-label">Critical</span>
                    </div>
                  </div>
                )}

                {brandStats && (
                  <div className="yl-card-stats">
                    <div className="yl-stat-box">
                      <span className="yl-stat-value">{brandStats.brands}</span>
                      <span className="yl-stat-label">Brands</span>
                    </div>
                    <div className="yl-stat-box">
                      <span className="yl-stat-value yl-stat-opp">{brandStats.totalStores.toLocaleString()}</span>
                      <span className="yl-stat-label">Total Stores</span>
                    </div>
                    <div className="yl-stat-box">
                      <span className="yl-stat-value yl-stat-occupied">{brandStats.occupiedStores.toLocaleString()}</span>
                      <span className="yl-stat-label">Occupied</span>
                    </div>
                  </div>
                )}

                <div className="yl-card-items-preview">
                  {list.items.length === 0 ? (
                    <div className="yl-card-empty">No items in this list yet</div>
                  ) : list.list_type === 'store' ? (
                    <>
                      {list.items.slice(0, 3).map((item: any) => {
                        const poi = list.storeDetails?.get(item.poi_id || '');
                        return (
                          <div key={item.id} className="yl-preview-item yl-preview-store">
                            <div className="yl-preview-store-info">
                              <span className="yl-type-dot store"></span>
                              <div className="yl-preview-store-text">
                                <span className="yl-preview-store-name">{poi?.brand_name || poi?.site || item.poi_id || '-'}{(poi as any)?.store_number ? ` ${(poi as any).store_number}` : ''}</span>
                                {poi && (
                                  <span className="yl-preview-store-address">
                                    {[poi.address, poi.geo?.city, poi.geo?.state_code, poi.geo?.zipcode].filter(Boolean).join(', ')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {list.items.length > 3 && (
                        <div className="yl-preview-more">+{list.items.length - 3} more</div>
                      )}
                    </>
                  ) : (
                    <>
                      {list.items.slice(0, 3).map((item: any) => {
                        const brand = list.brandDetails?.get(item.brand_name || '');
                        return (
                          <div key={item.id} className="yl-preview-item yl-preview-store">
                            <div className="yl-preview-store-info">
                              <span className="yl-type-dot brand"></span>
                              <div className="yl-preview-store-text">
                                <span className="yl-preview-store-name">{item.brand_name || '-'}</span>
                                {brand && (
                                  <span className="yl-preview-store-address">
                                    {brand.total_stores.toLocaleString()} stores
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {list.items.length > 3 && (
                        <div className="yl-preview-more">+{list.items.length - 3} more</div>
                      )}
                    </>
                  )}
                </div>

                <div className="yl-card-footer">
                  <span className="yl-card-open-link" onClick={e => { e.stopPropagation(); openDetail(list); }}>
                    <HiOutlineExternalLink size={13} />
                    Open list
                  </span>
                  {!list.isShared && (
                    <div className="yl-card-menu-wrap">
                      <button className="yl-card-menu-btn" onClick={e => toggleMenu(e, cardKey)}>
                        <HiOutlineDotsVertical size={16} />
                      </button>
                      {menuOpen === cardKey && (
                        <div className="yl-card-dropdown" onClick={e => e.stopPropagation()}>
                          <button className="yl-dropdown-item" onClick={e => handleShare(e, list)}>
                            <HiOutlineShare size={14} /> Share
                          </button>
                          <button className="yl-dropdown-item" onClick={e => handleStartRename(e, cardKey, list.list_name)}>
                            <HiOutlinePencil size={14} /> Rename
                          </button>
                          {deleteConfirm === cardKey ? (
                            <div className="yl-dropdown-confirm">
                              <span>Delete this list?</span>
                              <div className="yl-dropdown-confirm-btns">
                                <button className="yl-confirm-yes" onClick={() => handleDeleteList(list.list_name, cardKey)}>Delete</button>
                                <button className="yl-confirm-no" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button className="yl-dropdown-item yl-dropdown-delete" onClick={() => setDeleteConfirm(cardKey)}>
                              <HiOutlineTrash size={14} /> Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className="yl-card yl-card-create" onClick={handleOpenNewListModal}>
            <div className="yl-create-card-content">
              <span className="yl-create-card-icon">
                <HiOutlinePlus size={24} />
              </span>
              <span className="yl-create-card-title">Create New List</span>
              <span className="yl-create-card-subtitle">Organize brands and stores into custom lists</span>
            </div>
          </div>
        </div>
      )}

      {detailList && detailListData && (
        <div className="yl-modal-backdrop" onClick={() => setDetailList(null)}>
          <div className="yl-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="yl-detail-header">
              <div className="yl-detail-header-info">
                <h2 className="yl-detail-title">{detailListData.list_name}</h2>
                <span className="yl-detail-meta">
                  {detailListData.count} item{detailListData.count !== 1 ? 's' : ''} &middot; {detailListData.list_type === 'brand' ? 'Brands' : 'Stores'}
                </span>
              </div>
              <button className="yl-modal-close" onClick={() => setDetailList(null)}>
                <HiOutlineX size={18} />
              </button>
            </div>

            <div className="yl-detail-body">
              {detailListData.items.length === 0 ? (
                <div className="yl-detail-empty">No items in this list yet.</div>
              ) : detailListData.list_type === 'store' ? (
                <div className="yl-detail-items">
                  {detailListData.items.map((item: any) => {
                    const poi = detailListData.storeDetails?.get(item.poi_id || '');
                    const oppCount = poi ? getOpportunityCount(poi) : 0;
                    return (
                      <div key={item.id} className="yl-detail-item" onClick={() => handleItemClick(item)}>
                        <div className="yl-detail-item-info">
                          <span className="yl-type-dot store"></span>
                          <div className="yl-detail-store-text">
                            <span className="yl-detail-item-name">{poi?.brand_name || poi?.site || item.poi_id || '-'}{(poi as any)?.store_number ? ` ${(poi as any).store_number}` : ''}</span>
                            {poi?.geo && (
                              <span className="yl-detail-store-address">
                                {[poi.address, poi.geo.city, poi.geo.state_code, poi.geo.zipcode].filter(Boolean).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        {poi && (
                          <span className={`yl-preview-opp-count ${oppCount > 0 ? 'has-opp' : ''}`}>
                            {oppCount}
                          </span>
                        )}
                        {(!detailListData.isShared || detailListData.sharedPermission === 'edit') && (
                          <button
                            className="yl-remove-btn"
                            onClick={e => { e.stopPropagation(); handleRemoveItem(item.id, detailListData.list_name); }}
                            title="Remove"
                          >
                            <HiOutlineX size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="yl-detail-items">
                  {detailListData.items.map((item: any) => (
                    <div key={item.id} className="yl-detail-item" onClick={() => handleItemClick(item)}>
                      <div className="yl-detail-item-info">
                        <span className="yl-type-dot brand"></span>
                        <span className="yl-detail-item-name">{item.brand_name || '-'}</span>
                      </div>
                      <span className="yl-detail-item-date">{formatDate(item.created_at)}</span>
                      {(!detailListData.isShared || detailListData.sharedPermission === 'edit') && (
                        <button
                          className="yl-remove-btn"
                          onClick={e => { e.stopPropagation(); handleRemoveItem(item.id, detailListData.list_name); }}
                          title="Remove"
                        >
                          <HiOutlineX size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {shareListName && (
        <div className="yl-modal-backdrop" onClick={() => setShareListName(null)}>
          <div className="yl-modal yl-share-modal" onClick={e => e.stopPropagation()}>
            <div className="yl-modal-header">
              <h2>Share "{shareListName}"</h2>
              <button className="yl-modal-close" onClick={() => setShareListName(null)}>
                <HiOutlineX size={18} />
              </button>
            </div>

            <div className="yl-modal-body">
              <div className="yl-form-group">
                <label className="yl-form-label">Add people</label>
                <div className="yl-share-search-row">
                  <div className="yl-share-search-wrap">
                    <input
                      type="text"
                      className="yl-form-input"
                      placeholder="Search by name or email..."
                      value={shareSearch}
                      onChange={e => handleShareSearchChange(e.target.value)}
                      autoFocus
                    />
                    {searchLoading && <span className="yl-share-search-spinner" />}
                  </div>
                  <CustomDropdown
                    className="yl-permission-dropdown"
                    value={shareDefaultPermission}
                    options={[
                      { label: 'View', value: 'view' },
                      { label: 'Edit', value: 'edit' },
                    ]}
                    onChange={val => setShareDefaultPermission(val as 'view' | 'edit')}
                  />
                </div>
                <span className="yl-share-hint">Only @gatekeepersystems.com users</span>

                {shareSuggestions.length > 0 && (
                  <div className="yl-share-suggestions">
                    {shareSuggestions.map(u => (
                      <div key={u.id} className="yl-share-suggestion" onClick={() => handleShareWithUser(u)}>
                        <span className="yl-share-avatar">{(u.name || u.email).charAt(0).toUpperCase()}</span>
                        <div className="yl-share-suggestion-info">
                          <span className="yl-share-suggestion-name">{u.name || u.email.split('@')[0]}</span>
                          <span className="yl-share-suggestion-email">{u.email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {shareSearch.trim().length >= 2 && !searchLoading && shareSuggestions.length === 0 && (
                  <div className="yl-share-no-results">No matching users found</div>
                )}
              </div>

              <div className="yl-form-group">
                <label className="yl-form-label">People with access</label>
                {shareLoading ? (
                  <div className="yl-share-loading">Loading...</div>
                ) : sharedWith.length === 0 ? (
                  <div className="yl-share-empty">Not shared with anyone yet</div>
                ) : (
                  <div className="yl-share-list">
                    {sharedWith.map(entry => (
                      <div key={entry.id} className="yl-share-entry">
                        <span className="yl-share-avatar">
                          {(entry.shared_with_user.name || entry.shared_with_user.email).charAt(0).toUpperCase()}
                        </span>
                        <div className="yl-share-entry-info">
                          <span className="yl-share-entry-name">
                            {entry.shared_with_user.name || entry.shared_with_user.email.split('@')[0]}
                          </span>
                          <span className="yl-share-entry-email">{entry.shared_with_user.email}</span>
                        </div>
                        <CustomDropdown
                          className="yl-permission-dropdown yl-permission-inline"
                          value={entry.permission || 'view'}
                          options={[
                            { label: 'View', value: 'view' },
                            { label: 'Edit', value: 'edit' },
                          ]}
                          onChange={val => handlePermissionChange(entry.id, val as 'view' | 'edit')}
                        />
                        <button className="yl-share-remove" onClick={() => handleUnshare(entry.id)} title="Remove access">
                          <HiOutlineX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="yl-form-group">
                <label className="yl-form-label">General access</label>
                <div className="yl-org-access-row">
                  <div className="yl-org-access-info">
                    <span className="yl-org-access-icon">
                      <HiOutlineGlobe size={18} />
                    </span>
                    <div className="yl-org-access-text">
                      <span className="yl-org-access-title">
                        {shareOrgAccess ? 'Anyone in the organization' : 'Restricted'}
                      </span>
                      <span className="yl-org-access-desc">
                        {shareOrgAccess
                          ? `Anyone at Gatekeeper Systems can ${shareOrgAccess}`
                          : 'Only people with access can open'}
                      </span>
                    </div>
                  </div>
                  <CustomDropdown
                    className="yl-permission-dropdown"
                    value={shareOrgAccess || 'restricted'}
                    options={[
                      { label: 'Restricted', value: 'restricted' },
                      { label: 'View', value: 'view' },
                      { label: 'Edit', value: 'edit' },
                    ]}
                    onChange={val => handleOrgAccessChange(val === 'restricted' ? null : val as 'view' | 'edit')}
                  />
                </div>
                <div className="yl-copy-link-row">
                  <input
                    className="yl-copy-link-input"
                    readOnly
                    value={`${window.location.origin}/dashboard/list-stores/${shareListId}`}
                  />
                  <button
                    className="yl-copy-link-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/dashboard/list-stores/${shareListId}`);
                      toast.success('Link copied to clipboard');
                    }}
                  >
                    Copy link
                  </button>
                </div>
              </div>
            </div>

            <div className="yl-modal-footer">
              <button className="yl-modal-create" onClick={() => setShareListName(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {showNewListModal && (
        <div className="yl-modal-backdrop" onClick={() => setShowNewListModal(false)}>
          <div className="yl-modal" onClick={e => e.stopPropagation()}>
            <div className="yl-modal-header">
              <h2>Create New List</h2>
              <button className="yl-modal-close" onClick={() => setShowNewListModal(false)}>
                <HiOutlineX size={18} />
              </button>
            </div>

            <div className="yl-modal-body">
              <div className="yl-form-group">
                <label className="yl-form-label">Name</label>
                <input
                  type="text"
                  className="yl-form-input"
                  placeholder="e.g. High Priority Retailers"
                  value={newListName}
                  onChange={e => setNewListName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateNewList(); }}
                  autoFocus
                />
              </div>

              <div className="yl-form-group">
                <label className="yl-form-label">Description</label>
                <textarea
                  className="yl-form-textarea"
                  placeholder="Optional description for this list..."
                  value={newListDescription}
                  onChange={e => setNewListDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="yl-form-group">
                <label className="yl-form-label">Type</label>
                <div className="yl-type-options">
                  <label className={`yl-type-option ${newListType === 'brand' ? 'active' : ''}`}>
                    <input type="radio" name="listType" value="brand" checked={newListType === 'brand'} onChange={() => setNewListType('brand')} />
                    <span className="yl-type-option-dot brand"></span>
                    <span>Brands</span>
                  </label>
                  <label className={`yl-type-option ${newListType === 'store' ? 'active' : ''}`}>
                    <input type="radio" name="listType" value="store" checked={newListType === 'store'} onChange={() => setNewListType('store')} />
                    <span className="yl-type-option-dot store"></span>
                    <span>Stores</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="yl-modal-footer">
              <button className="yl-modal-cancel" onClick={() => setShowNewListModal(false)}>Cancel</button>
              <button className="yl-modal-create" onClick={handleCreateNewList}>Create List</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default YourLists;
